import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, InputAdornment, Link, Skeleton, Stack, TextField, Typography, Pagination, } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme, alpha } from '@mui/material/styles';
import { useLocale } from '../../../locale/LocaleProvider';
import { getPermitPageStrings } from '../../../locale/permitPageStrings';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getIconByName } from '../../../utils/iconMap';
import NavBar from '../NavBar/NavBar';
import SystemBannerFromGrid from '../../LandingHero/SystemBannerFromGrid';
import { portalContentMaxWidthSx, portalPageHorizontalPaddingSx } from '../../../theme/portalLayout';
import { fetchAgenciesFromSidebarDepartmentGrid, fetchDepartmentAgenciesPage, fetchPermitsForAgency, fetchPermitsFromSidebarAgencyGrid, findLandingDepartmentHero, } from '../../../services/departmentPermitsApi';
import { getCachedEnvConfig, loadEnvConfig, readDepartmentAgenciesGridId, readDepartmentAgenciesGridPageSize, readDepartmentAgencyPermitsGridId, readDepartmentAgencyPermitsGridPageSize, readSidebarBase, } from '../../../services/envConfig';
import { isInternalAppPath, navigateOrAssign, splitPathAndSearch } from '../../../utils/internalPath';
import { VIEW_DETAILS_QUERY, isCmsViewerSameOriginUrl, isEmbedUrlAllowed, isHttpUrlForEmbed, isProcessFlowDetailsEmbedUrl, rawViewDetailsQueryValue, resolveToAbsoluteUrl, } from '../../../utils/permitDetailsEmbed';
import { resolvePublicUrl } from '../../../utils/publicUrl';
import { preconnectOrigin } from '../../../utils/preconnect';
import { useFlowViewerHost } from '../FlowViewer/FlowViewerHost';
import { loadAgencyLogoMap, resolveAgencyLogoFile, resolveAgencyLogoPublicUrl } from '../../../services/agencyLogoMap';
import { resolveDepartmentHeroBackground } from '../../../services/departmentHeroMap';
import { resolvePermitPageStaticDepartment, resolvePermitPageDepartmentId } from '../../../utils/resolvePermitPageDepartment';
import { cmsPortalTypography } from '../../../theme/cmsTypography';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchLandingGrids } from '../../../store/landingGridsSlice';
import { catalogItemToPermitListDto, filterPermitsCatalogForDepartment, groupCatalogByAgencyId, permitCountByAgencyFromCatalogItems, } from '../../../services/departmentPermitsCatalogBridge';
function permitMatchesClientSearch(p, q) {
    if (!q)
        return true;
    const parts = [p.title, p.description, p.dateLabel, p.timeframe, p.priceRange, p.scope, p.recordId]
        .filter((x) => x != null && String(x).trim() !== '')
        .map((x) => String(x).toLowerCase());
    return parts.some((text) => text.includes(q));
}
function agencyMatchesClientSearch(a, q) {
    if (!q)
        return true;
    const parts = [a.id, a.title, a.subtitle]
        .filter((x) => x != null && String(x).trim() !== '')
        .map((x) => String(x).toLowerCase());
    return parts.some((text) => text.includes(q));
}
/** Ensures app-relative paths work with `resolvePublicUrl` (leading `/`). */
function normalizeAppImagePath(raw) {
    if (raw == null)
        return '';
    const t = raw.trim();
    if (!t)
        return '';
    if (/^https?:\/\//i.test(t) || t.startsWith('data:') || t.startsWith('//'))
        return t;
    return t.startsWith('/') ? t : `/${t}`;
}
/** Filter loaded permit rows; when q is empty, returns `raw` as-is. */
function filterPermitsForClientSearch(raw, q) {
    if (!raw?.length)
        return raw ?? [];
    if (!q)
        return raw;
    return raw.filter((p) => permitMatchesClientSearch(p, q));
}
/** A-Z by title, independent of API/grid row order. */
function sortPermitsAscending(list) {
    return [...list].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}
/**
 * Up to `max` tag strings from API-backed fields only — same order as the grid mapping (cost, time, date, scope).
 * Values are shown as-is with no UI-invented field titles.
 */
function permitTagValuesFromRow(p, max = 3) {
    const out = [];
    const push = (s) => {
        const t = s?.trim();
        if (t)
            out.push(t);
    };
    push(p.priceRange);
    push(p.timeframe);
    push(p.dateLabel);
    push(p.scope);
    return out.slice(0, max);
}
/** Theme palette slots for the three permit tags: info → warning → error (MUI `palette.*`). */
const PERMIT_TAG_PALETTE_KEYS = ['info', 'warning', 'error'];
/** API value chips — `belowTitle`: wrap under heading; `rightRail`: vertical stack in a side column (md+). */
function PermitTagList({ tagValues, theme, keyPrefix, layout, }) {
    if (!tagValues.length)
        return null;
    const rail = layout === 'rightRail';
    return (_jsx(Box, { component: "ul", sx: {
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'flex',
            flexDirection: rail ? 'column' : 'row',
            flexWrap: rail ? 'nowrap' : 'wrap',
            alignItems: rail ? 'stretch' : 'flex-start',
            gap: rail ? 0.65 : 0.6,
            width: 'auto',
            flex: '0 1 auto',
            ...(!rail
                ? {
                    rowGap: 0.55,
                }
                : {}),
        }, children: tagValues.map((text, ti) => {
            const chipText = '#074078';
            const chipBg = '#ffffff';
            const chipBorder = '#d7ecff';
            return (_jsx(Box, { component: "li", sx: {
                    maxWidth: rail ? '100%' : '100%',
                    flexShrink: rail ? 1 : 0,
                    px: 1.15,
                    py: 0.45,
                    borderRadius: 16,
                    fontSize: theme.typography.caption.fontSize,
                    fontWeight: 600,
                    lineHeight: 1.35,
                    color: chipText,
                    bgcolor: chipBg,
                    border: `1px solid ${chipBorder}`,
                    wordBreak: 'break-word',
                    boxShadow: `0 1px 2px ${alpha(chipBorder, 0.04)}`,
                    animation: 'permitTagPop 0.42s cubic-bezier(0.34, 1.2, 0.64, 1) both',
                    animationDelay: `${ti * 0.08}s`,
                    transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${alpha(chipBorder, 0.12)}`,
                    },
                }, children: text }, `${keyPrefix}-tag-${ti}`));
        }) }));
}
/** Permits shown per page inside each agency accordion (additional pages use Pagination). */
const PERMITS_PAGE_SIZE = 5;
const DepartmentPermitsPage = ({ nav, departments = {}, agenciesHeading = 'Agencies', agenciesSubheading = '', searchPlaceholder = 'Search ...', searchButtonLabel = 'Search', searchActionUrl: _searchActionUrl, agenciesApiUrlTemplate, permitsApiUrlTemplate, systemBanner, }) => {
    const theme = useTheme();
    const { locale } = useLocale();
    const ps = useMemo(() => getPermitPageStrings(locale), [locale]);
    /** Permit page surfaces & accents — from `appTheme` (`theme.ts`): primary, secondary, grey, success, warning. */
    const permitUi = useMemo(() => {
        const p = theme.palette;
        const shadowBase = p.grey[900];
        return {
            ink: p.text.primary,
            inkTitle: p.grey[800],
            inkSoft: p.grey[600],
            textMuted: p.text.secondary,
            textBody: p.grey[700],
            tanBorder: p.grey[200],
            accentHover: p.secondary.dark,
            agencyIconBg: alpha(p.primary.main, 0.08),
            agencyIconBorder: alpha(p.primary.main, 0.22),
            detailsBg: p.grey[50],
            detailsEdge: p.grey[200],
            cardShadow: `0 1px 3px ${alpha(shadowBase, 0.06)}`,
            cardShadowHover: `0 12px 40px -12px ${alpha(shadowBase, 0.14)}`,
            permitRowShadow: `0 1px 2px ${alpha(shadowBase, 0.04)}`,
            permitRowShadowHover: `0 6px 24px -6px ${alpha(shadowBase, 0.12)}`,
            calendarBlue: p.primary.main,
            approvalGreen: p.success.main,
            costAmber: p.warning.main,
            permitIconBg: alpha(p.secondary.main, 0.12),
            accentBar: p.secondary.main,
            pageBg: p.background.default,
            paper: p.background.paper,
        };
    }, [theme]);
    const cms = useMemo(() => cmsPortalTypography(theme), [theme]);
    const dispatch = useAppDispatch();
    const landingGrids = useAppSelector((s) => s.landingGrids);
    const navigate = useNavigate();
    const { departmentId: departmentIdParam } = useParams();
    /** Clean `:departmentId` (strip accidental query junk, decode). Used for APIs and display. */
    const departmentId = useMemo(() => {
        const raw = (departmentIdParam ?? '').trim();
        if (!raw)
            return '';
        const noQuery = raw.split('?')[0]?.trim() ?? '';
        try {
            return decodeURIComponent(noQuery).trim();
        }
        catch {
            return noQuery;
        }
    }, [departmentIdParam]);
    /** Numeric department id the Flow Builder grids actually store — resolves slug routes
     *  (`/permits/commerce`) to the id (`/permits/1` uses) so the agencies/permits grid filters
     *  match real data instead of the literal URL slug. Used for API calls only; `departmentId`
     *  (the raw slug) stays the one used for display/links. */
    const apiDepartmentId = useMemo(() => resolvePermitPageDepartmentId(departments, departmentId), [departments, departmentId]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState(() => searchParams.get('q')?.trim() ?? '');
    /** 1-based permit list page per agency id (accordion body). */
    const [permitPageByAgency, setPermitPageByAgency] = useState({});
    const [apiDepartment, setApiDepartment] = useState(null);
    const [agencies, setAgencies] = useState([]);
    const [agenciesLoading, setAgenciesLoading] = useState(true);
    const [agenciesError, setAgenciesError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    /** Minimum skeleton beat: false right after an accordion opens, flips true after a short delay so the
     *  loading skeleton is always visible for a moment (even when permits are already cached). */
    const [openReady, setOpenReady] = useState(false);
    const [permitsByAgency, setPermitsByAgency] = useState({});
    const [permitsLoading, setPermitsLoading] = useState({});
    const [permitsError, setPermitsError] = useState({});
    const [envViewDetailsUrl, setEnvViewDetailsUrl] = useState('');
    const [agencyLogoMap, setAgencyLogoMap] = useState(null);
    const permitsByAgencyRef = useRef(permitsByAgency);
    permitsByAgencyRef.current = permitsByAgency;
    /** When an accordion opens, show the skeleton for a short minimum beat before revealing content. */
    useEffect(() => {
        if (!expanded) {
            setOpenReady(false);
            return;
        }
        setOpenReady(false);
        const t = window.setTimeout(() => setOpenReady(true), 650);
        return () => window.clearTimeout(t);
    }, [expanded]);
    /** Agencies with a permits request in flight — synchronous dedupe (state `permitsLoading` updates too late to guard concurrent calls). */
    const permitsInFlightRef = useRef(new Set());
    useEffect(() => {
        let active = true;
        void loadEnvConfig().then((env) => {
            if (!active || !env)
                return;
            const url = env.ProcessFlowDetailsUrl ?? env.PermitViewDetailsUrl;
            if (typeof url === 'string' && url.trim().length > 0) {
                setEnvViewDetailsUrl(url);
                /** Warm the Flow Viewer origin now so the details iframe connects faster when View Details is clicked. */
                preconnectOrigin(url);
            }
        });
        return () => {
            active = false;
        };
    }, []);
    useEffect(() => {
        void loadAgencyLogoMap().then(setAgencyLogoMap);
    }, []);
    useEffect(() => {
        if (landingGrids.status === 'idle') {
            void dispatch(fetchLandingGrids());
        }
    }, [dispatch, landingGrids.status]);
    const catalogForDepartment = useMemo(() => departmentId
        ? filterPermitsCatalogForDepartment(landingGrids.permitsCatalog, departmentId, landingGrids.departmentsRaw)
        : [], [departmentId, landingGrids.permitsCatalog, landingGrids.departmentsRaw]);
    const catalogGroupedByAgency = useMemo(() => groupCatalogByAgencyId(catalogForDepartment), [catalogForDepartment]);
    const permitCountByAgencyFromCatalog = useMemo(() => permitCountByAgencyFromCatalogItems(catalogForDepartment), [catalogForDepartment]);
    /** Hero derived from the departments grid already loaded into Redux — replaces the duplicate master-grid fetch. */
    const landingDeptHero = useMemo(() => departmentId
        ? findLandingDepartmentHero(departmentId, landingGrids.departmentsRaw) ?? null
        : null, [departmentId, landingGrids.departmentsRaw]);
    /** API hero wins (page-JSON branch); otherwise fall back to the Redux landing grid row. */
    const departmentHero = apiDepartment ?? landingDeptHero;
    const staticDept = useMemo(() => departmentId ? resolvePermitPageStaticDepartment(departments, departmentId, departmentHero) : undefined, [departments, departmentId, departmentHero]);
    /** Do not use bundled page JSON hero when the agencies API failed — avoids flashing static copy instead of grid data. */
    const staticForHero = agenciesError ? undefined : staticDept;
    const hero = useMemo(() => {
        const api = departmentHero;
        const st = staticForHero;
        const humanTitle = departmentId &&
            departmentId
                .replace(/[_-]+/g, ' ')
                .trim()
                .replace(/\b\w/g, (c) => c.toUpperCase());
        const resolvedTitle = (api?.title ?? st?.title ?? '').trim() || humanTitle || (departmentId ? `Department ${departmentId}` : '');
        const mappedBg = resolveDepartmentHeroBackground({ departmentId });
        const rawBg = mappedBg ?? (st?.backgroundImage ?? api?.backgroundImage ?? '');
        const rawLogo = st?.logoImage ?? api?.logoImage ?? '';
        return {
            title: resolvedTitle,
            subtitle: st?.subtitle ?? api?.subtitle,
            /** Prefer Flow/API hero; page JSON fills gaps only when agencies loaded successfully. */
            icon: st?.icon ?? api?.icon,
            backgroundImage: normalizeAppImagePath(rawBg),
            logoImage: rawLogo.trim() ? normalizeAppImagePath(rawLogo) : undefined,
            iconAccentColor: st?.iconAccentColor ?? api?.iconAccentColor ?? '#2e7d32',
        };
    }, [departmentHero, staticForHero, departmentId]);
    /** Wait for agencies (+ department hero from grid) so we do not flash errors while the request is slow. */
    const showHeroSpinner = Boolean(departmentId) && agenciesLoading;
    const heroBg = hero.backgroundImage || '';
    const isHttpBg = /^https?:\/\//i.test(heroBg);
    /** WV tiles used as full-bleed department heroes (not compact “logo strip” mode). */
    const heroPathNorm = heroBg.split('?')[0]?.split('#')[0] ?? '';
    const isWvDepartmentHeroPhoto = /^\/images\/WV\/(revenue|health)\.png$/i.test(heroPathNorm);
    /** Full-bleed photo: remote URLs, legacy header asset, scenic West Virginia paths, or WV revenue/health hero PNGs (not other `/images/WV/` logos). */
    const hasScenicOrHeaderBg = isHttpBg ||
        heroBg === '/images/header-bg.png' ||
        isWvDepartmentHeroPhoto ||
        (Boolean(heroBg) && heroBg.startsWith('/images/') && !heroBg.startsWith('/images/WV/'));
    const hasWvLogoTile = Boolean(hero.logoImage?.startsWith('/images/WV/'));
    const bgIsWvLogoAsset = Boolean(heroBg) && heroBg.startsWith('/images/WV/');
    const logoMode = !hasScenicOrHeaderBg && (hasWvLogoTile || bgIsWvLogoAsset);
    const photoLift = { xs: 0, md: 0 };
    /** Gradient over full-bleed `cover` photos keeps text readable on the left. */
    const overlay = `linear-gradient(105deg, ${alpha(theme.palette.background.paper, 0.68)} 0%, ${alpha(theme.palette.background.paper, 0.32)} 26%, ${alpha(theme.palette.background.paper, 0.08)} 44%, transparent 56%, ${alpha(theme.palette.primary.dark, 0.22)} 100%)`;
    useEffect(() => {
        if (!departmentId) {
            setAgenciesLoading(false);
            setAgenciesError(null);
            setApiDepartment(null);
            setAgencies([]);
            setExpanded(false);
            setPermitsByAgency({});
            setPermitsLoading({});
            setPermitsError({});
            return;
        }
        let cancelled = false;
        setAgenciesLoading(true);
        setAgenciesError(null);
        setApiDepartment(null);
        setAgencies([]);
        setExpanded(false);
        setPermitsByAgency({});
        setPermitsLoading({});
        setPermitsError({});
        void (async () => {
            try {
                const env = await loadEnvConfig();
                const sidebar = readSidebarBase(env);
                const gridId = readDepartmentAgenciesGridId(env);
                const gridPageSize = readDepartmentAgenciesGridPageSize(env);
                /** Flow Builder grid when env is configured — numeric ids use `eq`, slug segments (e.g. `commerce`) use `contains`. */
                const useSidebarGrid = Boolean(sidebar && gridId);
                if (useSidebarGrid) {
                    /** Department hero comes from the landing departments grid already in Redux (see `landingDeptHero`), so we skip the duplicate master-grid request here. */
                    const data = await fetchAgenciesFromSidebarDepartmentGrid(sidebar, gridId, apiDepartmentId, gridPageSize);
                    if (!cancelled) {
                        setApiDepartment(data.department ?? null);
                        setAgencies(data.agencies);
                    }
                }
                else {
                    const data = await fetchDepartmentAgenciesPage(departmentId, agenciesApiUrlTemplate);
                    if (!cancelled) {
                        setApiDepartment(data.department ?? null);
                        setAgencies(data.agencies);
                    }
                }
            }
            catch (e) {
                if (!cancelled) {
                    const msg = e instanceof Error ? e.message : getPermitPageStrings(locale).loadDepartmentFailed;
                    setAgenciesError(msg);
                }
            }
            finally {
                if (!cancelled)
                    setAgenciesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [departmentId, apiDepartmentId, agenciesApiUrlTemplate, locale]);
    /** Prefill accordion permit lists from landing `permitsCatalog` when the grid already returned rows for this department. */
    useEffect(() => {
        if (!departmentId || agenciesLoading || !agencies.length)
            return;
        if (!catalogForDepartment.length)
            return;
        const byAgency = groupCatalogByAgencyId(catalogForDepartment);
        setPermitsByAgency((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const a of agencies) {
                const rows = byAgency[a.id];
                if (!rows?.length)
                    continue;
                if (next[a.id] !== undefined)
                    continue;
                next[a.id] = rows.map(catalogItemToPermitListDto);
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [departmentId, agencies, agenciesLoading, catalogForDepartment]);
    const loadPermitsIfNeeded = useCallback(async (agencyId) => {
        if (permitsByAgencyRef.current[agencyId] !== undefined)
            return;
        if (permitsInFlightRef.current.has(agencyId))
            return;
        if (!departmentId)
            return;
        permitsInFlightRef.current.add(agencyId);
        setPermitsLoading((m) => {
            if (m[agencyId])
                return m;
            return { ...m, [agencyId]: true };
        });
        setPermitsError((m) => {
            const next = { ...m };
            delete next[agencyId];
            return next;
        });
        try {
            await loadEnvConfig();
            const env = getCachedEnvConfig() ?? {};
            const sidebar = readSidebarBase(env);
            const permitsGridId = readDepartmentAgencyPermitsGridId(env);
            const permitsPageSize = readDepartmentAgencyPermitsGridPageSize(env);
            const useSidebarPermitsGrid = Boolean(sidebar && permitsGridId);
            const processFlowDetailsUrl = typeof env.ProcessFlowDetailsUrl === 'string' && env.ProcessFlowDetailsUrl.trim()
                ? env.ProcessFlowDetailsUrl.trim()
                : typeof env.PermitViewDetailsUrl === 'string' && env.PermitViewDetailsUrl.trim()
                    ? env.PermitViewDetailsUrl.trim()
                    : '';
            const list = useSidebarPermitsGrid
                ? await fetchPermitsFromSidebarAgencyGrid(sidebar, permitsGridId, apiDepartmentId, agencyId, permitsPageSize, { processFlowDetailsUrl: processFlowDetailsUrl || undefined })
                : await fetchPermitsForAgency(agencyId, permitsApiUrlTemplate);
            setPermitsByAgency((prev) => ({ ...prev, [agencyId]: list }));
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : getPermitPageStrings(locale).loadPermitsFailed;
            setPermitsError((m) => ({ ...m, [agencyId]: msg }));
        }
        finally {
            permitsInFlightRef.current.delete(agencyId);
            setPermitsLoading((m) => ({ ...m, [agencyId]: false }));
        }
    }, [permitsApiUrlTemplate, departmentId, apiDepartmentId, locale]);
    const handleAccordionChange = (agencyId) => (_, isExpanded) => {
        setExpanded(isExpanded ? agencyId : false);
        if (isExpanded)
            void loadPermitsIfNeeded(agencyId);
    };
    /** Hash route: `#/permits/:dept?agency=:id` — read before search memos (hooks order). */
    const agencyQueryParam = searchParams.get('agency') ?? searchParams.get('agencyId');
    const agencyDeepLinkHandledRef = useRef(null);
    /** Client-side filter: agencies by name/id; include agency if loaded permits contain a match. */
    const searchNormalized = useMemo(() => query.trim().toLowerCase(), [query]);
    const agenciesMatchingSearch = useMemo(() => {
        const q = searchNormalized;
        if (!q)
            return agencies;
        return agencies.filter((a) => {
            if (agencyMatchesClientSearch(a, q))
                return true;
            const loaded = permitsByAgency[a.id];
            if (loaded?.length)
                return loaded.some((p) => permitMatchesClientSearch(p, q));
            const catRows = catalogGroupedByAgency[a.id];
            if (catRows?.length)
                return catRows.some((p) => permitMatchesClientSearch(catalogItemToPermitListDto(p), q));
            return false;
        });
    }, [agencies, permitsByAgency, searchNormalized, catalogGroupedByAgency]);
    useEffect(() => {
        setPermitPageByAgency({});
    }, [searchNormalized]);
    useEffect(() => {
        setPermitPageByAgency({});
    }, [departmentId]);
    useEffect(() => {
        agencyDeepLinkHandledRef.current = null;
    }, [departmentId]);
    useEffect(() => {
        const raw = agencyQueryParam?.trim();
        if (!departmentId)
            return;
        if (!raw) {
            agencyDeepLinkHandledRef.current = null;
            return;
        }
        let decoded = raw;
        try {
            decoded = decodeURIComponent(raw);
        }
        catch {
            decoded = raw;
        }
        decoded = decoded.trim();
        if (!decoded) {
            agencyDeepLinkHandledRef.current = null;
            return;
        }
        const sig = `${departmentId}|${decoded}`;
        if (agencyDeepLinkHandledRef.current === sig)
            return;
        if (agenciesLoading)
            return;
        const match = agencies.find((a) => String(a.id).trim() === decoded);
        if (!match) {
            agencyDeepLinkHandledRef.current = sig;
            setSearchParams((p) => {
                const n = new URLSearchParams(p);
                n.delete('agency');
                n.delete('agencyId');
                return n;
            }, { replace: true });
            return;
        }
        const agencyId = match.id;
        agencyDeepLinkHandledRef.current = sig;
        setExpanded(agencyId);
        void loadPermitsIfNeeded(agencyId);
        const tid = window.setTimeout(() => {
            document.getElementById(`permit-agency-${agencyId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            setSearchParams((p) => {
                const n = new URLSearchParams(p);
                n.delete('agency');
                n.delete('agencyId');
                return n;
            }, { replace: true });
        }, 320);
        return () => window.clearTimeout(tid);
    }, [
        agencyQueryParam,
        agenciesLoading,
        agencies,
        agenciesMatchingSearch,
        departmentId,
        loadPermitsIfNeeded,
        setSearchParams,
    ]);
    const legacyViewDetails = searchParams.get(VIEW_DETAILS_QUERY);
    const legacySearchString = searchParams.toString();
    /** Old links used `?viewDetails=` on the department URL — forward to the full-page details route. */
    useEffect(() => {
        if (!departmentId || !legacyViewDetails)
            return;
        navigate({
            pathname: `/permits/${departmentId}/details`,
            search: legacySearchString ? `?${legacySearchString}` : '',
        }, { replace: true });
    }, [departmentId, legacyViewDetails, legacySearchString, navigate]);
    const openViewDetailsInPage = useCallback((rawUrl, scope) => {
        const absolute = resolveToAbsoluteUrl(rawUrl.trim());
        if (!absolute || !departmentId)
            return;
        /** Carries the permit/agency this "View Details" click was for onto the details route, so its
         *  System Banner can be scoped to them — the embed URL alone has no permit/agency identity. */
        const addBannerScope = (qs) => {
            if (scope?.agencyTitle?.trim())
                qs.set('agency', scope.agencyTitle.trim());
            if (scope?.permitTitle?.trim())
                qs.set('permitTitle', scope.permitTitle.trim());
        };
        /**
         * Flow viewer URLs (`#/ProcessFlows/…/view`) open the permit details route and load in its iframe.
         * Same-origin URLs with pathname `/` would otherwise match `isCmsViewerSameOriginUrl` and nest the
         * Flow hash inside the CMS router incorrectly.
         */
        if (isProcessFlowDetailsEmbedUrl(absolute) && isEmbedUrlAllowed(absolute)) {
            const qs = new URLSearchParams();
            qs.set(VIEW_DETAILS_QUERY, rawViewDetailsQueryValue(absolute));
            addBannerScope(qs);
            navigate({
                pathname: `/permits/${departmentId}/details`,
                search: qs.toString(),
            });
            return;
        }
        try {
            const u = new URL(absolute);
            if (isCmsViewerSameOriginUrl(absolute)) {
                navigate({
                    pathname: u.pathname,
                    search: u.search || undefined,
                    hash: u.hash || undefined,
                });
                return;
            }
        }
        catch {
            const { pathname, search } = splitPathAndSearch(absolute);
            if (isInternalAppPath(pathname)) {
                navigateOrAssign(navigate, `${pathname}${search}`);
                return;
            }
        }
        if (!isEmbedUrlAllowed(absolute)) {
            if (isHttpUrlForEmbed(absolute) || !/^[a-z][a-z0-9+.-]*:/i.test(absolute)) {
                window.location.href = absolute;
            }
            return;
        }
        const qs = new URLSearchParams();
        qs.set(VIEW_DETAILS_QUERY, rawViewDetailsQueryValue(absolute));
        addBannerScope(qs);
        navigate({
            pathname: `/permits/${departmentId}/details`,
            search: qs.toString(),
        });
    }, [navigate, departmentId]);
    const handleDetailsViewClick = useCallback((targetUrl, scope) => {
        const url = targetUrl.trim();
        if (!url)
            return;
        openViewDetailsInPage(url, scope);
    }, [openViewDetailsInPage]);
    /**
     * Hover/press prefetch (Option 1): start loading the permit in the warm hidden iframe before the click,
     * so opening its details page reveals almost instantly. Only applies to Flow Viewer embed URLs.
     */
    const flowHost = useFlowViewerHost();
    const prefetchDetailsView = useCallback((targetUrl) => {
        if (!flowHost)
            return;
        const absolute = resolveToAbsoluteUrl(targetUrl.trim());
        if (absolute && isProcessFlowDetailsEmbedUrl(absolute) && isEmbedUrlAllowed(absolute))
            flowHost.prefetch(absolute);
    }, [flowHost]);
    /** Bad or empty `:departmentId` (e.g. hash/router desync) — send users to landing instead of a dead-end alert. */
    if (!departmentId) {
        return _jsx(Navigate, { to: "/", replace: true });
    }
    const handleSearch = (e) => {
        e.preventDefault();
        setSearchParams((prev) => {
            const n = new URLSearchParams(prev);
            const t = query.trim();
            if (t)
                n.set('q', t);
            else
                n.delete('q');
            return n;
        }, { replace: true });
    };
    if (showHeroSpinner) {
        return (_jsx(Box, { component: "main", sx: {
                minHeight: '50vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
            }, children: _jsx(CircularProgress, { color: "primary" }) }));
    }
    const backgroundImage = hero.backgroundImage;
    const logoImage = hero.logoImage;
    const heroWatermarkImage = backgroundImage || '';
    const heroWatermarkResolved = heroWatermarkImage
        ? resolvePublicUrl(heroWatermarkImage) ?? heroWatermarkImage
        : '';
    const hasHeroBackgroundImage = Boolean(heroWatermarkImage);
    const isLogoHeroImage = logoMode && hasHeroBackgroundImage;
    /** Separate badge only when CMS/API provides an explicit logo — not the same asset repeated from the hero photo. */
    const trimmedDepartmentLogo = (logoImage ?? '').trim();
    const showDepartmentLogoBadge = Boolean(trimmedDepartmentLogo);
    const departmentIconResolved = trimmedDepartmentLogo
        ? resolvePublicUrl(trimmedDepartmentLogo) ?? trimmedDepartmentLogo
        : '';
    const useLightPrimaryHero = logoMode && !hasHeroBackgroundImage;
    const heroTitleColor = useLightPrimaryHero ? theme.palette.primary.main : theme.palette.primary.contrastText;
    const heroSubtitleColor = useLightPrimaryHero
        ? alpha(theme.palette.primary.main, 0.82)
        : alpha(theme.palette.primary.contrastText, 0.95);
    const heroOverlayBackground = isLogoHeroImage
        ? `linear-gradient(110deg, ${alpha(theme.palette.primary.main, 0.56)} 0%, ${alpha(theme.palette.primary.dark, 0.62)} 58%, ${alpha(theme.palette.primary.dark, 0.68)} 100%)`
        : useLightPrimaryHero
            ? 'none'
            : overlay;
    return (_jsxs(Box, { component: "main", sx: { bgcolor: permitUi.pageBg, minHeight: '100%' }, children: [nav && (_jsx(Box, { sx: { width: '100%' }, children: _jsx(NavBar, { ...nav, embedInHero: true }) })), systemBanner ? _jsx(SystemBannerFromGrid, { ...systemBanner }) : null, _jsxs(Box, { sx: {
                    position: 'relative',
                    overflow: 'hidden',
                    /** Compact strip; background `<img>` uses `cover` and fills the hero edge-to-edge (no horizontal padding on this wrapper). */
                    minHeight: logoMode
                        ? { xs: 160, md: 200 }
                        : hasHeroBackgroundImage
                            ? { xs: 158, sm: 178, md: 192 }
                            : { xs: 'min-content', md: 'min(26vh, 280px)' },
                    pt: { xs: 0.15, sm: 0.3, md: 0.4 },
                    pb: logoMode ? { xs: 0.5, md: 0.75 } : { xs: 0.65, md: 0.9 },
                    bgcolor: useLightPrimaryHero ? alpha(theme.palette.primary.main, 0.1) : 'primary.main',
                }, children: [hasHeroBackgroundImage ? (_jsx(Box, { "aria-hidden": true, sx: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: photoLift,
                            zIndex: 0,
                            overflow: 'hidden',
                            height: '100%',
                            minHeight: 0,
                            bgcolor: useLightPrimaryHero ? alpha(theme.palette.primary.main, 0.08) : 'primary.main',
                        }, children: _jsx(Box, { component: "img", src: heroWatermarkResolved, alt: "", sx: {
                                display: 'block',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center center',
                                opacity: isLogoHeroImage ? { xs: 0.78, md: 0.82 } : { xs: 0.55, md: 0.58 },
                            } }) })) : null, _jsx(Box, { "aria-hidden": true, sx: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: photoLift,
                            zIndex: 1,
                            background: heroOverlayBackground,
                            pointerEvents: 'none',
                        } }), _jsx(Box, { sx: { position: 'relative', zIndex: 3, width: '100%' }, children: _jsxs(Box, { sx: {
                                ...portalContentMaxWidthSx,
                                ...portalPageHorizontalPaddingSx,
                                mt: nav ? { xs: 0.1, md: 0.35 } : 0,
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: { xs: 1.5, md: 2 },
                                alignItems: 'center',
                            }, children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'center', sm: 'center' },
                                        gap: 2,
                                        textAlign: { xs: 'center', sm: 'left' },
                                    }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "h3", component: "h1", sx: {
                                                        m: 0,
                                                        color: heroTitleColor,
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                        textShadow: useLightPrimaryHero
                                                            ? 'none'
                                                            : `0 2px 12px ${alpha(theme.palette.primary.dark, 0.35)}`,
                                                    }, children: hero.title }), hero.subtitle && (_jsx(Typography, { variant: "subtitle1", sx: {
                                                        mt: 0.65,
                                                        color: heroSubtitleColor,
                                                        fontWeight: 500,
                                                        lineHeight: 1.45,
                                                        textShadow: useLightPrimaryHero
                                                            ? 'none'
                                                            : `0 1px 8px ${alpha(theme.palette.primary.dark, 0.3)}`,
                                                    }, children: hero.subtitle }))] }), showDepartmentLogoBadge ? (_jsx(Box, { sx: {
                                                width: { xs: 72, sm: 80 },
                                                height: { xs: 72, sm: 80 },
                                                borderRadius: 2,
                                                bgcolor: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }, children: _jsx(Box, { component: "img", src: departmentIconResolved, alt: `${hero.title} logo`, sx: { width: { xs: 64, sm: 70 }, height: { xs: 64, sm: 70 }, objectFit: 'contain' } }) })) : null] }), _jsxs(Box, { component: "form", onSubmit: handleSearch, sx: {
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        bgcolor: alpha(theme.palette.background.paper, useLightPrimaryHero ? 0.98 : 0.97),
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: { xs: 2.5, sm: 3 },
                                        boxShadow: useLightPrimaryHero
                                            ? `0 4px 24px ${alpha(theme.palette.grey[900], 0.08)}`
                                            : `0 12px 40px ${alpha(theme.palette.primary.dark, 0.22)}`,
                                        overflow: 'hidden',
                                        border: `1px solid ${alpha(useLightPrimaryHero ? theme.palette.text.primary : theme.palette.primary.contrastText, useLightPrimaryHero ? 0.08 : 0.2)}`,
                                        alignSelf: { xs: 'stretch', md: 'center' },
                                    }, children: [_jsx(TextField, { fullWidth: true, placeholder: searchPlaceholder, value: query, onChange: (e) => setQuery(e.target.value), variant: "standard", InputProps: {
                                                disableUnderline: true,
                                                startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { sx: { color: 'text.secondary', ml: 0.5, mr: 0.5, fontSize: 22 } }) })),
                                                sx: { pl: 1.35, pr: 1.75, py: 0.95, ...cms.searchFieldInput },
                                            }, sx: { flex: 1, m: 0 } }), _jsx(Button, { type: "submit", variant: "contained", color: "secondary", sx: {
                                                borderRadius: 0,
                                                px: { xs: 2.5, sm: 3.5 },
                                                minWidth: { xs: 100, sm: 120 },
                                                boxShadow: 'none',
                                                ...cms.searchFieldButton,
                                                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark ?? theme.palette.secondary.main} 100%)`,
                                                '&:hover': { boxShadow: 'none', filter: 'brightness(1.05)' },
                                            }, children: searchButtonLabel })] })] }) })] }), _jsxs(Box, { sx: {
                    bgcolor: permitUi.pageBg,
                    py: logoMode ? { xs: 1, md: 1.35 } : { xs: 1.15, md: 1.5 },
                    ...portalPageHorizontalPaddingSx,
                    mt: { xs: -0.35, md: -0.5 },
                    position: 'relative',
                    overflow: 'hidden',
                }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                            position: 'absolute',
                            inset: 0,
                            zIndex: 0,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }, children: heroWatermarkResolved ? (_jsx(Box, { sx: {
                                position: 'absolute',
                                inset: 0,
                                backgroundImage: `url("${String(heroWatermarkResolved).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center center',
                                backgroundRepeat: 'no-repeat',
                                opacity: 0.1,
                            } })) : null }), _jsxs(Box, { sx: {
                            ...portalContentMaxWidthSx,
                            position: 'relative',
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            borderRadius: { xs: 1, sm: 1.5 },
                            border: `2px solid ${alpha(theme.palette.grey[300], 0.45)}`,
                            boxShadow: '0 3px 24px rgba(15, 23, 42, 0.055)',
                            px: { xs: 0.5, sm: 0.75, md: 1 },
                            py: { xs: 0.6, sm: 0.85, md: 1 },
                        }, children: [_jsx(Box, { sx: {
                                    mb: 1.35,
                                    pb: 1.35,
                                    borderBottom: `1px solid ${alpha(permitUi.accentBar, 0.2)}`,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                                                width: 3,
                                                height: 32,
                                                borderRadius: 1,
                                                background: `linear-gradient(180deg, ${permitUi.accentBar} 0%, ${alpha(permitUi.accentBar, 0.5)} 100%)`,
                                                flexShrink: 0,
                                            } }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h5", component: "h2", sx: {
                                                        fontWeight: 800,
                                                        color: permitUi.ink,
                                                        letterSpacing: '-0.02em',
                                                        lineHeight: 1.25,
                                                    }, children: agenciesHeading }), agenciesSubheading.trim() ? (_jsx(Typography, { component: "p", variant: "body2", sx: {
                                                        mt: 0.65,
                                                        mb: 0,
                                                        lineHeight: 1.55,
                                                        fontSize: '0.92rem',
                                                        color: theme.palette.text.secondary,
                                                        //color: permitUi.textMuted,
                                                        //fontWeight: 400,
                                                        maxWidth: 640,
                                                    }, children: agenciesSubheading.trim() })) : null] })] }) }), agenciesError && (_jsx(Alert, { severity: "error", sx: { mb: 1.5, py: 0.5 }, children: agenciesError })), agenciesLoading && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 2 }, children: _jsx(CircularProgress, { size: 24, color: "primary" }) })), !agenciesLoading &&
                                agencies.length > 0 &&
                                agenciesMatchingSearch.length === 0 &&
                                Boolean(searchNormalized) && (_jsx(Typography, { variant: "body2", sx: { py: 2, color: permitUi.textMuted }, children: ps.searchNoResults })), !agenciesLoading && agenciesMatchingSearch.length > 0 && (_jsx(_Fragment, { children: _jsx(Stack, { spacing: 1.25, sx: {
                                        mt: 0,
                                        '@keyframes permitsAccordionLift': {
                                            '0%': { opacity: 0, transform: 'translateY(10px)' },
                                            '100%': { opacity: 1, transform: 'translateY(0)' },
                                        },
                                        '@keyframes permitRowIn': {
                                            '0%': { opacity: 0, transform: 'translateY(14px)' },
                                            '100%': { opacity: 1, transform: 'translateY(0)' },
                                        },
                                        '@keyframes permitTagPop': {
                                            '0%': { opacity: 0, transform: 'scale(0.94) translateY(4px)' },
                                            '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                                        },
                                    }, children: agenciesMatchingSearch.map((agency, listIdx) => {
                                        const isOpen = expanded === agency.id;
                                        const AgencyIcon = getIconByName(agency.icon ?? 'Info');
                                        const agencyLogoFile = agencyLogoMap &&
                                            resolveAgencyLogoFile(agencyLogoMap, {
                                                name: agency.title,
                                                recordId: agency.id,
                                            });
                                        const agencyLogoSrc = agencyLogoFile && agencyLogoMap
                                            ? resolveAgencyLogoPublicUrl(agencyLogoMap, agencyLogoFile)
                                            : undefined;
                                        const permitsRaw = permitsByAgency[agency.id];
                                        const permitsFiltered = filterPermitsForClientSearch(permitsRaw, searchNormalized);
                                        const permits = sortPermitsAscending(permitsFiltered);
                                        const permitTotalPages = Math.max(1, Math.ceil(permits.length / PERMITS_PAGE_SIZE));
                                        const permitPageRaw = permitPageByAgency[agency.id] ?? 1;
                                        const permitPage = Math.min(Math.max(1, permitPageRaw), permitTotalPages);
                                        const permitsPaged = permits.slice((permitPage - 1) * PERMITS_PAGE_SIZE, (permitPage - 1) * PERMITS_PAGE_SIZE + PERMITS_PAGE_SIZE);
                                        const catalogRowsForAgency = catalogGroupedByAgency[agency.id];
                                        const catalogCount = catalogRowsForAgency?.length ?? 0;
                                        const chipBaseCount = (permitsRaw?.length ?? 0) > 0 ? permitsRaw.length : catalogCount;
                                        const searchMatchChipCount = permitsRaw !== undefined
                                            ? permits.length
                                            : (catalogRowsForAgency?.filter((p) => permitMatchesClientSearch(catalogItemToPermitListDto(p), searchNormalized)).length ?? 0);
                                        const showPermitChip = searchNormalized
                                            ? searchMatchChipCount > 0
                                            : chipBaseCount > 0;
                                        const loading = permitsLoading[agency.id];
                                        const err = permitsError[agency.id];
                                        /** Show the skeleton when this open accordion is still loading OR within the minimum open beat. */
                                        const showSkeleton = isOpen && !err && (permitsRaw === undefined || loading || !openReady);
                                        return (_jsxs(Accordion, { id: `permit-agency-${agency.id}`, expanded: isOpen, onChange: handleAccordionChange(agency.id), disableGutters: true, elevation: 0, TransitionProps: { timeout: 380 }, sx: {
                                                borderRadius: 1.25,
                                                overflow: 'hidden',
                                                border: `2px solid ${alpha(theme.palette.grey[300], 0.45)}`,
                                                bgcolor: isOpen ? theme.palette.background.paper : alpha(theme.palette.grey[50], 0.85),
                                                boxShadow: isOpen
                                                    ? `0 4px 18px ${alpha(theme.palette.primary.main, 0.08)}`
                                                    : `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
                                                transition: 'box-shadow 0.25s ease, background-color 0.25s ease',
                                                animation: `permitsAccordionLift 0.4s ease ${listIdx * 0.04}s both`,
                                                '&:before': { display: 'none' },
                                                '&:hover': {
                                                    bgcolor: theme.palette.background.paper,
                                                },
                                            }, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(Box, { sx: {
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: '#e6f7ff',
                                                            border: '1px solid #bfeeff',
                                                        }, children: _jsx(ExpandMoreIcon, { sx: {
                                                                color: theme.palette.primary.main,
                                                                fontSize: 20,
                                                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            } }) }), sx: {
                                                        px: { xs: 1.25, sm: 1.5 },
                                                        py: { xs: 0.85, sm: 1 },
                                                        minHeight: 48,
                                                        bgcolor: 'transparent',
                                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                                            mr: -0.25,
                                                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        },
                                                        '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                                                            transform: 'rotate(180deg)',
                                                        },
                                                        borderBottom: isOpen
                                                            ? `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`
                                                            : 'none',
                                                        borderLeft: `3px solid ${isOpen ? theme.palette.secondary.main : 'transparent'}`,
                                                        '& .MuiAccordionSummary-content': {
                                                            alignItems: 'center',
                                                            gap: { xs: 1.15, sm: 1.5 },
                                                            my: 0,
                                                        },
                                                    }, children: [_jsx(Box, { sx: {
                                                                width: { xs: 36, sm: 40 },
                                                                height: { xs: 36, sm: 40 },
                                                                borderRadius: { xs: 2.5, sm: 3 },
                                                                background: agencyLogoSrc
                                                                    ? alpha(theme.palette.common.white, 0.92)
                                                                    : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                overflow: 'hidden',
                                                            }, children: agencyLogoSrc ? (_jsx(Box, { component: "img", src: agencyLogoSrc, alt: "", sx: {
                                                                    width: '78%',
                                                                    height: '78%',
                                                                    objectFit: 'contain',
                                                                    display: 'block',
                                                                } })) : (_jsx(AgencyIcon, { sx: { color: theme.palette.primary.main, fontSize: { xs: 20, sm: 22 } } })) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: {
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        alignItems: 'center',
                                                                        gap: 0.75,
                                                                        columnGap: 1,
                                                                    }, children: [_jsx(Typography, { component: "span", sx: {
                                                                                ...cms.agencyAccordionTitle,
                                                                                color: permitUi.ink,
                                                                            }, children: agency.title }), showPermitChip && (_jsx(Chip, { size: "small", label: searchNormalized
                                                                                ? ps.matchChip(searchMatchChipCount)
                                                                                : ps.permitChip(chipBaseCount), sx: {
                                                                                height: 22,
                                                                                fontWeight: 600,
                                                                                fontSize: theme.typography.caption.fontSize,
                                                                                bgcolor: '#e6f7ff',
                                                                                color: theme.palette.primary.main,
                                                                                border: '1px solid #bfeeff',
                                                                                '& .MuiChip-label': { px: 0.85 },
                                                                            } }))] }), agency.subtitle && (_jsx(Typography, { component: "span", sx: {
                                                                        ...cms.agencyAccordionSubtitle,
                                                                        mt: 0.35,
                                                                        display: 'block',
                                                                        color: permitUi.textMuted,
                                                                    }, children: agency.subtitle }))] })] }), _jsxs(AccordionDetails, { sx: {
                                                        // px: { xs: 1.15, sm: 1.35 },
                                                        // pt: { xs: 1.1, sm: 1.25 },
                                                        // pb: { xs: 1.25, sm: 1.5 },
                                                        p: 1,
                                                        borderTop: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
                                                        position: 'relative',
                                                        background: `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.grey[100]} 42%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 20,
                                                            right: 20,
                                                            height: 2,
                                                            borderRadius: 2,
                                                            background: `linear-gradient(90deg, transparent 0%, ${theme.palette.secondary.main} 50%, transparent 100%)`,
                                                            opacity: 0.55,
                                                        },
                                                    }, children: [systemBanner && isOpen ? (_jsx(SystemBannerFromGrid, { ...systemBanner, agencyName: agency.title, sticky: false })) : null, showSkeleton && (
                                                        /* Animated skeleton preview of the permit cards — always shown for a short beat when
                                                           the accordion opens, and until permits finish loading. */
                                                        _jsx(Stack, { spacing: 1.75, sx: { pt: 0.35 }, "aria-busy": "true", "aria-label": "Loading permits", children: Array.from({ length: 3 }).map((_, i) => (_jsxs(Box, { sx: {
                                                                    display: 'flex',
                                                                    gap: 1.5,
                                                                    alignItems: 'flex-start',
                                                                    borderRadius: 1,
                                                                    border: `1px solid ${alpha(theme.palette.grey[300], 0.45)}`,
                                                                    background: theme.palette.background.paper,
                                                                    p: 1.5,
                                                                }, children: [_jsx(Skeleton, { variant: "rounded", width: 44, height: 44, animation: "wave", sx: { flexShrink: 0 } }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Skeleton, { variant: "text", width: "55%", height: 24, animation: "wave" }), _jsx(Skeleton, { variant: "text", width: "90%", animation: "wave" }), _jsx(Skeleton, { variant: "text", width: "72%", animation: "wave" }), _jsxs(Box, { sx: { display: 'flex', gap: 1, mt: 1 }, children: [_jsx(Skeleton, { variant: "rounded", width: 68, height: 22, animation: "wave" }), _jsx(Skeleton, { variant: "rounded", width: 92, height: 22, animation: "wave" }), _jsx(Skeleton, { variant: "rounded", width: 54, height: 22, animation: "wave" })] })] })] }, i))) })), err && !loading && (_jsx(Alert, { severity: "error", variant: "outlined", sx: {
                                                                mt: 0,
                                                                py: 0.5,
                                                                borderRadius: 2,
                                                                bgcolor: alpha(theme.palette.background.paper, 0.85),
                                                                '& .MuiAlert-message': { py: 0.5 },
                                                            }, children: err })), !showSkeleton &&
                                                            !err &&
                                                            permitsRaw &&
                                                            permitsRaw.length > 0 &&
                                                            permits.length === 0 &&
                                                            searchNormalized && (_jsx(Typography, { variant: "body2", sx: { py: 1, color: permitUi.textMuted }, children: ps.noPermitsMatchSearch })), !showSkeleton &&
                                                            !err &&
                                                            permits.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Stack, { spacing: 1.75, sx: { pt: 0.35, position: 'relative', zIndex: 1 }, children: permitsPaged.map((p, pi) => {
                                                                        const PIcon = getIconByName(p.icon ?? 'Description');
                                                                        const tagValues = permitTagValuesFromRow(p, 3);
                                                                        const hasDetailsCta = Boolean((p.detailsHref && p.detailsHref !== '#') || envViewDetailsUrl?.trim());
                                                                        const applyNowHref = p.applyNow?.trim();
                                                                        return (_jsx(Box, { sx: {
                                                                                position: 'relative',
                                                                                borderRadius: 1,
                                                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.45)}`,
                                                                                background: theme.palette.background.paper,
                                                                                boxShadow: `
                                    0 1px 2px ${alpha(theme.palette.common.black, 0.04)},
                                    0 12px 32px -8px ${alpha(theme.palette.primary.main, 0.1)}
                                  `,
                                                                                transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease',
                                                                                animation: `permitRowIn 0.85s cubic-bezier(0.22, 1, 0.36, 1) both`,
                                                                                animationDelay: `${Math.min(pi, 10) * 0.09}s`,
                                                                                '@media (prefers-reduced-motion: reduce)': {
                                                                                    animation: 'none',
                                                                                    transition: 'none',
                                                                                },
                                                                                '&:hover': {
                                                                                    transform: 'translateY(-4px)',
                                                                                    boxShadow: `
                                      0 4px 8px ${alpha(theme.palette.common.black, 0.06)},
                                      0 20px 48px -12px ${alpha(theme.palette.primary.main, 0.18)}
                                    `,
                                                                                },
                                                                                '&:hover .permits-tile-icon': {
                                                                                    bgcolor: alpha(theme.palette.secondary.main, 0.14),
                                                                                },
                                                                            }, children: _jsx(Box, { sx: {
                                                                                    p: { xs: 1.5, sm: 1.75 },
                                                                                    display: 'flex',
                                                                                    flexDirection: { xs: 'column', md: 'row' },
                                                                                    gap: { xs: 1.25, md: 1 },
                                                                                    alignItems: { xs: 'stretch', md: 'flex-start' },
                                                                                }, children: _jsxs(Box, { sx: {
                                                                                        display: 'flex',
                                                                                        gap: 1.5,
                                                                                        flex: 1,
                                                                                        minWidth: 0,
                                                                                        alignItems: 'flex-start',
                                                                                    }, children: [_jsx(Box, { className: "permits-tile-icon", sx: {
                                                                                                width: 38,
                                                                                                height: 38,
                                                                                                borderRadius: 2.5,
                                                                                                bgcolor: alpha(theme.palette.primary.main, 0.09),
                                                                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                flexShrink: 0,
                                                                                                transition: 'background-color 0.3s ease, transform 0.3s ease',
                                                                                            }, children: _jsx(PIcon, { sx: { color: theme.palette.primary.main, fontSize: 18 } }) }), _jsxs(Box, { sx: {
                                                                                                flex: 1,
                                                                                                minWidth: 0,
                                                                                                display: 'flex',
                                                                                                flexDirection: 'column',
                                                                                                alignItems: 'stretch',
                                                                                                gap: { xs: 0.9, sm: 1.05 },
                                                                                            }, children: [_jsx(Box, { sx: {
                                                                                                        mt: 0.25
                                                                                                        //pb: 0.65,
                                                                                                        //borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                                                                    }, children: _jsx(Typography, { component: "h3", sx: {
                                                                                                            ...cms.permitCardTitle,
                                                                                                            color: permitUi.ink,
                                                                                                            pr: { sm: 1 },
                                                                                                            fontWeight: 600,
                                                                                                        }, children: p.title }) }), p.description?.trim() ? (_jsx(Typography, { component: "p", sx: {
                                                                                                        ...cms.permitBody,
                                                                                                        m: 0,
                                                                                                        mt: 0.15,
                                                                                                        color: permitUi.textBody,
                                                                                                        px: { xs: 0, sm: 0.25 },
                                                                                                        //py: { xs: 0.75, sm: 0.85 },
                                                                                                    }, children: p.description.trim() })) : null, _jsxs(Box, { sx: {
                                                                                                        display: 'flex',
                                                                                                        justifyContent: 'space-between',
                                                                                                        alignItems: 'center',
                                                                                                        flexWrap: 'wrap',
                                                                                                        gap: 1.5,
                                                                                                        //mt: 1,
                                                                                                    }, children: [_jsx(PermitTagList, { tagValues: tagValues, theme: theme, keyPrefix: `${agency.id}-${p.recordId ?? p.title}-bottom`, layout: "belowTitle" }), (hasDetailsCta || applyNowHref) && (_jsxs(Box, { sx: {
                                                                                                                display: 'flex',
                                                                                                                alignItems: 'center',
                                                                                                                justifyContent: 'flex-start',
                                                                                                                gap: 1.5,
                                                                                                                pt: 0.35,
                                                                                                                mt: 'auto',
                                                                                                            }, children: [applyNowHref && (_jsx(Button, { component: "a", href: applyNowHref, target: "_blank", rel: "noopener noreferrer", variant: "contained", color: "secondary", size: "small", sx: {
                                                                                                                        ...cms.permitCta,
                                                                                                                        borderRadius: 999,
                                                                                                                        px: 1.75,
                                                                                                                        py: 0.4,
                                                                                                                        minHeight: 0,
                                                                                                                        textTransform: 'none',
                                                                                                                    }, children: ps.applyNow })), hasDetailsCta && (_jsxs(Link, { component: "button", type: "button", underline: "none", sx: {
                                                                                                                        ...cms.permitCta,
                                                                                                                        position: 'relative',
                                                                                                                        display: 'inline-flex',
                                                                                                                        alignItems: 'center',
                                                                                                                        gap: 0.5,
                                                                                                                        overflow: 'visible',
                                                                                                                        lineHeight: 1.2,
                                                                                                                        color: theme.palette.primary.main,
                                                                                                                        cursor: 'pointer',
                                                                                                                        border: 'none',
                                                                                                                        borderRadius: 0,
                                                                                                                        px: 0,
                                                                                                                        py: 0,
                                                                                                                        fontFamily: 'inherit',
                                                                                                                        textTransform: 'none',
                                                                                                                        letterSpacing: '0.02em',
                                                                                                                        background: 'none',
                                                                                                                        boxShadow: 'none',
                                                                                                                        '&::after': {
                                                                                                                            display: 'none',
                                                                                                                        },
                                                                                                                        '&:hover': {
                                                                                                                            textDecoration: 'underline',
                                                                                                                            transform: 'none',
                                                                                                                            boxShadow: 'none',
                                                                                                                            filter: 'none',
                                                                                                                            '& .permits-chevron': {
                                                                                                                                transform: 'translateX(4px)',
                                                                                                                            },
                                                                                                                        },
                                                                                                                        '&:active': {
                                                                                                                            transform: 'none',
                                                                                                                        },
                                                                                                                    }, onMouseEnter: () => {
                                                                                                                        const href = p.detailsHref && p.detailsHref !== '#'
                                                                                                                            ? p.detailsHref
                                                                                                                            : envViewDetailsUrl?.trim() || '';
                                                                                                                        if (href)
                                                                                                                            prefetchDetailsView(href);
                                                                                                                    }, onFocus: () => {
                                                                                                                        const href = p.detailsHref && p.detailsHref !== '#'
                                                                                                                            ? p.detailsHref
                                                                                                                            : envViewDetailsUrl?.trim() || '';
                                                                                                                        if (href)
                                                                                                                            prefetchDetailsView(href);
                                                                                                                    }, onClick: (e) => {
                                                                                                                        e.preventDefault();
                                                                                                                        const href = p.detailsHref && p.detailsHref !== '#'
                                                                                                                            ? p.detailsHref
                                                                                                                            : envViewDetailsUrl?.trim() || '';
                                                                                                                        if (href)
                                                                                                                            handleDetailsViewClick(href, { agencyTitle: agency.title, permitTitle: p.title });
                                                                                                                    }, children: [ps.viewDetails, _jsx(ChevronRightIcon, { className: "permits-chevron", sx: {
                                                                                                                                fontSize: 18,
                                                                                                                                transition: 'transform 0.18s ease',
                                                                                                                                color: theme.palette.primary.main,
                                                                                                                            } })] }))] }))] })] })] }) }) }, `${agency.id}-${p.recordId ?? p.title}`));
                                                                    }) }), permitTotalPages > 1 && (_jsxs(Box, { sx: {
                                                                        pt: 1.35,
                                                                        mt: 0.35,
                                                                        borderTop: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                    }, children: [_jsx(Typography, { variant: "caption", component: "p", sx: {
                                                                                ...cms.permitPaginationRange,
                                                                                color: 'primary.main',
                                                                            }, children: ps.permitsRange((permitPage - 1) * PERMITS_PAGE_SIZE + 1, Math.min(permitPage * PERMITS_PAGE_SIZE, permits.length), permits.length) }), _jsx(Pagination, { count: permitTotalPages, page: permitPage, onChange: (_, p) => {
                                                                                setPermitPageByAgency((prev) => ({ ...prev, [agency.id]: p }));
                                                                            }, color: "primary", shape: "rounded", showFirstButton: true, showLastButton: true, size: "small", siblingCount: 0, sx: {
                                                                                '& .MuiPaginationItem-root': {
                                                                                    fontWeight: 600,
                                                                                    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease, color 0.2s ease',
                                                                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                                                                },
                                                                                '& .MuiPaginationItem-root:hover': {
                                                                                    transform: 'scale(1.08)',
                                                                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                                                },
                                                                                '& .Mui-selected': {
                                                                                    background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark ?? theme.palette.primary.main} 100%)`,
                                                                                    color: theme.palette.primary.contrastText,
                                                                                    borderColor: 'transparent',
                                                                                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                                                                                    '&:hover': {
                                                                                        background: `linear-gradient(145deg, ${theme.palette.primary.dark ?? theme.palette.primary.main} 0%, ${theme.palette.primary.main} 100%)`,
                                                                                        color: theme.palette.primary.contrastText,
                                                                                    },
                                                                                },
                                                                            } })] }))] })), !loading &&
                                                            !err &&
                                                            permitsRaw &&
                                                            permitsRaw.length === 0 && (_jsx(Box, { sx: {
                                                                mt: 0.15,
                                                                py: 1.35,
                                                                px: 1.5,
                                                                borderRadius: 1.5,
                                                                textAlign: 'center',
                                                                border: `1px dashed ${alpha(permitUi.inkTitle, 0.15)}`,
                                                                bgcolor: alpha(theme.palette.background.paper, 0.65),
                                                            }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                    color: permitUi.textMuted,
                                                                    fontWeight: 500,
                                                                    lineHeight: 1.5,
                                                                }, children: ps.noPermitsListed }) }))] })] }, agency.id));
                                    }) }) }))] })] })] }));
};
export default DepartmentPermitsPage;
